/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
      }
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      if (this.slider) {
        const slides = this.slider.querySelectorAll('.announcement__text');
        slides[0].classList.add('is-visible');

        const nextSlide = () => {
          if (!this.isPaused && theme.elementUtil.isInViewport(this)) {
            const currSlide = this.slider.querySelector('.announcement__text.is-visible');
            const currSlideIndex = Array.from(currSlide.parentNode.children)
              .indexOf(currSlide);
            const nextSlideIndex = currSlideIndex + 1 < slides.length ? currSlideIndex + 1 : 0;

            if (document.dir === 'rtl') {
              this.slider.scrollTo(
                -Math.abs(nextSlideIndex * slides[nextSlideIndex].clientWidth),
                0
              );
            } else {
              this.slider.scrollTo(slides[nextSlideIndex].offsetLeft - 32, 0);
            }

            slides[nextSlideIndex].classList.add('is-visible');
            slides[currSlideIndex].classList.remove('is-visible');
          }

          setTimeout(nextSlide, this.dataset.slideDelay);
        };
        setTimeout(nextSlide, this.dataset.slideDelay);
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
  const existing = document.querySelector('.mob__announcement-links');

  if (!theme.mediaMatches.md && !existing) {
    const mobNav = document.createElement('nav');
    mobNav.classList.add('mob__announcement-links');

    const ul = document.createElement('ul');
    ul.classList.add('secondary-nav');

    // Enterprise Search SVG (exactly as your theme uses)
    const searchIcon = `
      <svg width="21" height="23" viewBox="0 0 21 23" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon">
        <path d="M14.398 14.483 19 19.514l-1.186 1.014-4.59-5.017a8.317 8.317 0 0 1-4.888 1.578C3.732 17.089 0 13.369 0 8.779S3.732.472 8.336.472c4.603 0 8.335 3.72 8.335 8.307a8.265 8.265 0 0 1-2.273 5.704ZM8.336 15.53c3.74 0 6.772-3.022 6.772-6.75 0-3.729-3.031-6.75-6.772-6.75S1.563 5.051 1.563 8.78c0 3.728 3.032 6.75 6.773 6.75Z"/>
      </svg>
    `;

    // Enterprise-style FAQ icon (question bubble)
    const faqIcon = `
      <svg width="21" height="23" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon">
        <path d="M12 2C6.477 2 2 6.037 2 11c0 2.61 1.29 5.03 3.41 6.67V22l4.05-2.09c.83.21 1.68.32 2.54.32 5.523 0 10-4.037 10-9.23S17.523 2 12 2Zm.2 15.2h-1.9v-1.9h1.9v1.9Zm2.44-7.12c-.23.27-.52.5-.86.71-.35.21-.6.41-.76.6-.16.2-.24.46-.24.8v.4h-1.82v-.53c0-.47.1-.86.3-1.17.2-.31.54-.61 1.02-.9.39-.23.68-.46.87-.69.2-.23.29-.51.29-.84 0-.39-.14-.7-.43-.94-.28-.24-.67-.36-1.16-.36-.45 0-.84.1-1.15.31-.31.21-.53.51-.65.9l-1.74-.72c.22-.67.64-1.21 1.26-1.62.62-.41 1.39-.62 2.3-.62 1.12 0 2.03.27 2.74.8.71.53 1.06 1.29 1.06 2.27 0 .56-.12 1.03-.35 1.3Z"/>
      </svg>
    `;

    // Enterprise-style phone icon
    const phoneIcon = `
      <svg width="21" height="23" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon">
        <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.85 21 3 13.15 3 3a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.46.57 3.58a1 1 0 0 1-.24 1.01l-2.21 2.2Z"/>
      </svg>
    `;

    // Helper to build a link item
    const buildItem = (href, label, iconHtml) => `
      <li>
        <a class="secondary-nav__item secondary-nav__item--icon" href="${href}">
          ${iconHtml}
          <span>${label}</span>
        </a>
      </li>
    `;

    // 1) Search FIRST
    ul.innerHTML += buildItem('/search', 'Search', searchIcon);

    // 2) Clone announcement links but reorder:
    //    - Put FAQ-ish links next (non-tel)
    //    - Put phone LAST (tel:)
    const linksArr = Array.from(this.links || []);
    const phoneLinks = linksArr.filter((a) => (a.getAttribute('href') || '').startsWith('tel:'));
    const otherLinks = linksArr.filter((a) => !(a.getAttribute('href') || '').startsWith('tel:'));

    // Add other links (FAQ icon for all non-tel links)
    otherLinks.forEach((link) => {
      ul.innerHTML += buildItem(link.href, link.innerText, faqIcon);
    });

    // Add phone link(s) LAST (phone icon)
    phoneLinks.forEach((link) => {
      ul.innerHTML += buildItem(link.href, link.innerText, phoneIcon);
    });

    mobNav.appendChild(ul);
    this.menu.appendChild(mobNav);

  } else if (theme.mediaMatches.md && existing) {
    existing.remove();
  }
}

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
